import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { bcEmit, bcSubscribe, CHAN_AUTH } from "../lib/broadcast";
import FirstLoginWelcome from "../components/FirstLoginWelcome";
// iter316 (Feb 27 2026) — Auth-transition cleanup so a shared device
// doesn't leak the previous customer's Custom Studio draft / Maya chat
// to the next person.
import { clearCustomStudioOnAuthChange } from "../pages/shop/custom-studio/v4/lib/draftPersistence";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext({ user: null, loading: true, login: () => {}, logout: () => {}, openAuthModal: () => {}, closeAuthModal: () => {}, authModalOpen: false });

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [welcomeOpen, setWelcomeOpen] = useState(false);
    const welcomeShownRef = useRef(false);  // session-scoped guard — never show twice
    const isRemoteAuthRef = useRef(false);
    const oauthProcessedRef = useRef(false);
    const navigate = useNavigate();
    const location = useLocation();

    const check = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(r.data);
            // Re-open the signup form if this user is mid-onboarding (phone-only
            // user who closed the modal before completing Name + Email).
            if (r.data?.needs_profile) setAuthModalOpen(true);
            // iter206 follow-up — one-time welcome bento for users created
            // after iter206 ship date. `welcomeShownRef` guards against
            // double-mounting (StrictMode dev double-invoke, or sibling-tab
            // broadcasts triggering another check()).
            if (r.data?.needs_welcome && !welcomeShownRef.current && !r.data?.needs_profile) {
                welcomeShownRef.current = true;
                setWelcomeOpen(true);
            }
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // ---- OAuth return-leg handler ----
    // iter205 fix v4 (May 28 2026) — the OAuth exchange ran in a dedicated
    // <AuthCallback> route component that took over the entire screen with
    // a "Welcoming you back" overlay. Customers found the overlay jarring
    // ("no website does this"). Moved the exchange to AuthProvider so it
    // runs in the BACKGROUND while the regular page (ShopHome / wherever)
    // continues to render. No overlay. The header just morphs to
    // signed-in state once the exchange completes.
    useEffect(() => {
        if (oauthProcessedRef.current) return;
        const hash = window.location.hash || "";
        const m = hash.match(/session_id=([^&]+)/);
        if (!m) {
            check();
            return;
        }
        oauthProcessedRef.current = true;
        const session_id = decodeURIComponent(m[1]);
        // Clear the hash immediately so React doesn't re-enter this branch.
        try {
            window.history.replaceState(null, "", window.location.pathname + window.location.search);
        } catch { /* noop */ }

        // ---- Admin branch ----
        const adminExpected = (() => {
            try { return sessionStorage.getItem("jns_admin_expected_email") || ""; }
            catch { return ""; }
        })();
        if (adminExpected) {
            (async () => {
                try {
                    await axios.post(
                        `${API}/admin/auth/session`,
                        { session_id, expected_email: adminExpected },
                        { withCredentials: true },
                    );
                    try { sessionStorage.removeItem("jns_admin_expected_email"); } catch { /* noop */ }
                    navigate("/config", { replace: true });
                } catch (e) {
                    const status = e?.response?.status;
                    const detail = e?.response?.data?.detail;
                    try { sessionStorage.removeItem("jns_admin_expected_email"); } catch { /* noop */ }
                    if (status === 403) {
                        try { sessionStorage.setItem("jns_admin_denied_reason", detail || ""); } catch { /* noop */ }
                        navigate("/config/access-denied", { replace: true });
                    } else {
                        toast.error(detail || "Admin sign-in failed. Please try again.");
                        navigate("/config/login", { replace: true });
                    }
                } finally {
                    // Ensure customer auth state is also resolved so any
                    // useAuth consumers stop showing loading.
                    setLoading(false);
                }
            })();
            return;
        }

        // ---- Customer branch ----
        const returnTo = (() => {
            try {
                const t = sessionStorage.getItem("jns_auth_return_to") || "/";
                sessionStorage.removeItem("jns_auth_return_to");
                return t;
            } catch { return "/"; }
        })();
        (async () => {
            try {
                await axios.post(`${API}/auth/session`, { session_id }, { withCredentials: true });
                await check();
                // Only navigate if we're not already at the destination.
                // navigate-to-same-path is a no-op in react-router, which
                // is fine.
                if (returnTo && returnTo !== location.pathname + location.search) {
                    navigate(returnTo, { replace: true });
                }
            } catch {
                toast.error("Sign-in failed. Please try again.");
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Broadcast login/logout transitions to other tabs of the same browser.
    // Other tabs re-run `check()` to refresh their own /me state.
    // iter316 (Feb 27 2026) — Also fire local Custom Studio cleanup on:
    //   • logout (user → null) — so the next customer on a shared device
    //     doesn't inherit the previous draft / Maya chat.
    //   • switch (user A → user B) — so B doesn't see A's local state.
    // NOT on login (null → user) because the guest's in-flight design
    // should sync into their new account (existing lastSyncedAuthRef
    // effects in Stepper + Visual handle that push).
    const prevUserIdRef = useRef(undefined);
    useEffect(() => {
        const prev = prevUserIdRef.current;
        const cur = user?.user_id;
        prevUserIdRef.current = cur;
        if (prev === undefined) return; // initial mount — neither broadcast nor cleanup
        const isRemote = isRemoteAuthRef.current;
        if (isRemote) isRemoteAuthRef.current = false;

        if (prev && !cur) {
            // Local sign-out: broadcast + clean. Remote sign-out: clean only
            // (the broadcast already came FROM the sibling tab; re-emitting
            // would loop). Either way the on-device state needs scrubbing.
            if (!isRemote) bcEmit(CHAN_AUTH, { type: "logout" });
            try { clearCustomStudioOnAuthChange(); } catch { /* noop */ }
        } else if (!prev && cur) {
            // Sign-in (guest → user). KEEP local Custom Studio state so
            // the guest's in-flight design can sync into the new account
            // via Stepper/Visual's lastSyncedAuthRef effect. Just emit
            // the broadcast so sibling tabs refresh their /me.
            if (!isRemote) bcEmit(CHAN_AUTH, { type: "login" });
        } else if (prev !== cur) {
            // Switch (User A → User B). Clean so B never inherits A's
            // draft, Maya chat, or selfie cache.
            if (!isRemote) bcEmit(CHAN_AUTH, { type: "switch" });
            try { clearCustomStudioOnAuthChange(); } catch { /* noop */ }
        }
    }, [user]);

    // React to login/logout from sibling tabs by re-fetching our own /me
    useEffect(() => {
        return bcSubscribe(CHAN_AUTH, () => {
            isRemoteAuthRef.current = true;
            check();
        });
    }, [check]);

    const logout = async () => {
        try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { /* noop */ }
        try { localStorage.removeItem("jns_cart"); } catch { /* noop */ }
        try { localStorage.removeItem("jns_cart_anonymous"); } catch { /* noop */ }
        setUser(null);
        if (typeof window !== "undefined") {
            const path = window.location.pathname || "/";
            const isAuthOnly = (
                path.startsWith("/profile") ||
                path.startsWith("/orders") ||
                path.startsWith("/custom-orders") ||
                path.startsWith("/wishlist") ||
                path.startsWith("/jns-credit") ||
                path.startsWith("/notifications") ||
                path.startsWith("/account")
            );
            // iter206 follow-up #2 — only force a full reload when we're
            // navigating AWAY from a protected page. Otherwise let React
            // re-render in place; calling `window.location.replace(currentPath)`
            // triggered a needless full page refresh on every logout
            // ("logout shows 2 refreshes" user feedback).
            if (isAuthOnly) {
                window.location.replace("/");
            }
        }
    };

    const openAuthModal = useCallback(() => setAuthModalOpen(true), []);
    const closeAuthModal = useCallback(() => setAuthModalOpen(false), []);

    return (
        <AuthContext.Provider value={{ user, loading, refresh: check, logout, openAuthModal, closeAuthModal, authModalOpen }}>
            {children}
            <FirstLoginWelcome
                open={welcomeOpen}
                onClose={() => setWelcomeOpen(false)}
                userName={user?.name || ""}
            />
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

export const startGoogleLogin = () => {
    // Remember where the customer started so we can bring them back after sign-in
    try {
        const here = window.location.pathname + window.location.search;
        sessionStorage.setItem("jns_auth_return_to", here || "/");
    } catch { /* noop */ }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};
